import apiClient from '../apiClient';

class AdminService {
  // ── Stats ──────────────────────────────────────────────────────────────────
  async getStats() {
    return apiClient.get('/admin/stats');
  }

  // ── Companies ──────────────────────────────────────────────────────────────
  async getCompanies() {
    return apiClient.get('/admin/companies');
  }

  async updateCompanyStatus(companyId, { accountStatus, canPostJobs, adminNotes, testMode, allowMCP }) {
    return apiClient.patch(`/admin/companies/${companyId}/status`, {
      accountStatus,
      canPostJobs,
      adminNotes,
      testMode,
      allowMCP,
    });
  }

  async updateCompanySubscription(companyId, { subscriptionTier, subscriptionExpiresAt }) {
    return apiClient.patch(`/admin/companies/${companyId}/subscription`, {
      subscriptionTier,
      subscriptionExpiresAt,
    });
  }

  // Update company metadata (e.g. auth0Id)
  async updateCompany(companyId, updates) {
    return apiClient.patch(`/admin/companies/${companyId}`, updates);
  }

  // ── Job Seekers ────────────────────────────────────────────────────────────
  async getJobSeekers() {
    return apiClient.get('/admin/job-seekers');
  }

  async searchJobSeekers({ profileId, profileName, countryOfOrigin, abnNumber, phoneNumber, email, location } = {}, showAll = false, page = 0, pageSize = 50, cursor = null) {
    const qs = [];
    if (profileId) qs.push(`profileId=${encodeURIComponent(profileId)}`);
    if (profileName) qs.push(`profileName=${encodeURIComponent(profileName)}`);
    if (countryOfOrigin) qs.push(`countryOfOrigin=${encodeURIComponent(countryOfOrigin)}`);
    if (abnNumber) qs.push(`abnNumber=${encodeURIComponent(abnNumber)}`);
    if (phoneNumber) qs.push(`phoneNumber=${encodeURIComponent(phoneNumber)}`);
    if (email) qs.push(`email=${encodeURIComponent(email)}`);
    if (location) qs.push(`location=${encodeURIComponent(location)}`);
    if (showAll) qs.push('showAll=true');
    // cursor mode (default / showAll) vs offset mode (search with filters)
    if (cursor) {
      qs.push(`cursor=${encodeURIComponent(cursor)}`);
    } else {
      qs.push(`page=${page}`);
    }
    qs.push(`pageSize=${pageSize}`);
    const query = qs.length ? `?${qs.join('&')}` : '';
    return apiClient.get(`/admin/job-seekers/search${query}`);
  }

  async getJobSeekerInsights() {
    return apiClient.get('/admin/job-seekers/insights');
  }

  async updateJobSeekerStatus(profileId, { accountStatus, adminNotes, skillsVerified }) {
    return apiClient.patch(`/admin/job-seekers/${profileId}/status`, {
      accountStatus,
      adminNotes,
      skillsVerified,
    });
  }

  // Update job seeker metadata (e.g. auth0Id)
  async updateJobSeeker(profileId, updates) {
    return apiClient.patch(`/admin/job-seekers/${profileId}`, updates);
  }

  // ── Jobs ───────────────────────────────────────────────────────────────────
  async getJobs({ from, to, jobQuery, companyQuery, phoneQuery } = {}) {
    const qs = [];
    if (from) qs.push(`from=${encodeURIComponent(from)}`);
    if (to) qs.push(`to=${encodeURIComponent(to)}`);
    if (jobQuery) qs.push(`jobQuery=${encodeURIComponent(jobQuery)}`);
    if (companyQuery) qs.push(`companyQuery=${encodeURIComponent(companyQuery)}`);
    if (phoneQuery) qs.push(`phoneQuery=${encodeURIComponent(phoneQuery)}`);
    const query = qs.length ? `?${qs.join('&')}` : '';
    return apiClient.get(`/admin/jobs${query}`);
  }

  async updateJob(jobId, updates) {
    return apiClient.patch(`/admin/jobs/${jobId}`, updates);
  }

  // ── Applications ───────────────────────────────────────────────────────────
  async getApplications({ from, to, jobSeekerQuery, companyQuery, jobQuery } = {}) {
    const qs = [];
    if (from) qs.push(`from=${encodeURIComponent(from)}`);
    if (to) qs.push(`to=${encodeURIComponent(to)}`);
    if (jobSeekerQuery) qs.push(`jobSeekerQuery=${encodeURIComponent(jobSeekerQuery)}`);
    if (companyQuery) qs.push(`companyQuery=${encodeURIComponent(companyQuery)}`);
    if (jobQuery) qs.push(`jobQuery=${encodeURIComponent(jobQuery)}`);
    const query = qs.length ? `?${qs.join('&')}` : '';
    return apiClient.get(`/admin/applications${query}`);
  }

  async deleteApplication(applicationId) {
    return apiClient.delete(`/admin/applications/${applicationId}`);
  }

  // ── Admin Document Access ──────────────────────────────────────────────────
  async getDocumentDownloadUrl(documentId) {
    return apiClient.get(`/admin/documents/${documentId}/download-url`);
  }

  // ── Export ─────────────────────────────────────────────────────────────────
  async exportCompanies() {
    return apiClient.get('/admin/export/companies');
  }

  async exportJobSeekers() {
    return apiClient.get('/admin/export/job-seekers');
  }

  // ── Support Cases (Admin) ─────────────────────────────────────────────────
  async getSupportCases() {
    // Accept optional params object: { limit, nextToken, status }
    const args = arguments[0] || {}
    const qs = []
    if (args.limit) qs.push(`limit=${Number(args.limit)}`)
    if (args.nextToken) qs.push(`nextToken=${encodeURIComponent(args.nextToken)}`)
    if (args.status) qs.push(`status=${encodeURIComponent(args.status)}`)
    const query = qs.length ? `?${qs.join('&')}` : ''
    return apiClient.get(`/admin/support-cases${query}`)
  }

  // ── WhatsApp QRCodes ───────────────────────────────────────────────────────
  async getWhatsappQRCodes() {
    return apiClient.get('/admin/whatsapp-qrcodes');
  }

  // WhatsApp messages captured from incoming messages
  async getWhatsappMessages() {
    const args = arguments[0] || {}
    const qs = []
    if (args.limit) qs.push(`limit=${Number(args.limit)}`)
    if (args.nextToken) qs.push(`nextToken=${encodeURIComponent(args.nextToken)}`)
    const query = qs.length ? `?${qs.join('&')}` : ''
    return apiClient.get(`/admin/whatsapp-messages${query}`)
  }

  async repostWhatsappMessage(messageId) {
    return apiClient.post(`/admin/whatsapp-messages/${encodeURIComponent(messageId)}/repost`, {});
  }

  // ── Broadcast Rooms / Groups ───────────────────────────────────────────────
  async getBroadcastRooms() {
    return apiClient.get('/admin/broadcast/rooms');
  }

  async getBroadcastGroups() {
    return apiClient.get('/admin/broadcast/groups');
  }

  async addBroadcastGroup(payload) {
    return apiClient.post('/admin/broadcast/groups', payload);
  }

  async updateBroadcastGroup(groupId, updates) {
    return apiClient.patch(`/admin/broadcast/groups/${encodeURIComponent(groupId)}`, updates);
  }

  async deleteBroadcastGroup(groupId) {
    return apiClient.delete(`/admin/broadcast/groups/${encodeURIComponent(groupId)}`);
  }

  // Trigger remote groups reload on the notification service (Telegram/WhatsApp) via backend proxy
  async reloadNotificationGroups() {
    return apiClient.post('/admin/broadcast/reload');
  }

  // Trigger a manual restart request for the WhatsApp server (publishes to SNS)
  async restartWhatsappServer() {
    return apiClient.post('/admin/broadcast/restart-whatsapp');
  }

  async updateSupportCaseStatus(caseId, status) {
    return apiClient.patch(`/admin/support-cases/${caseId}/status`, { status });
  }

  async addSupportCaseComment(caseId, comment) {
    return apiClient.post(`/admin/support-cases/${caseId}/comment`, { comment });
  }

  async getSupportCaseAttachmentUrls(caseId) {
    return apiClient.get(`/admin/support-cases/${caseId}/attachments`);
  }

  async confirmNoShow(profileId) {
    return apiClient.patch(`/admin/job-seekers/${profileId}/no-show`, {});
  }

  async resetNoShow(profileId) {
    return apiClient.patch(`/admin/job-seekers/${profileId}/reset-no-show`, {});
  }

  // Fetch a single Cognito user by auth0Id / username
  async getCognitoUser(auth0Id) {
    return apiClient.get(`/admin/cognito-user/${encodeURIComponent(auth0Id)}`);
  }

  // ── Cognito User Management ────────────────────────────────────────────────
  async getCognitoUsers(paginationToken = null) {
    const qs = paginationToken ? `?paginationToken=${encodeURIComponent(paginationToken)}` : '';
    return apiClient.get(`/admin/cognito/users${qs}`);
  }

  async getCognitoAdmins() {
    return apiClient.get('/admin/cognito/admins');
  }

  async verifyCognitoUser(username) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/verify`, {});
  }

  async addCognitoUserToAdmin(username) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/add-to-admin`, {});
  }

  async disableCognitoUser(username) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/disable`, {});
  }

  async enableCognitoUser(username) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/enable`, {});
  }

  async updateCognitoUserPhone(username, phone) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/update-phone`, { phone });
  }

  async resetCognitoUserPassword(username) {
    return apiClient.post(`/admin/cognito/users/${encodeURIComponent(username)}/reset-password`, {});
  }

  // ── CSV Exports ────────────────────────────────────────────────────────────
  async exportCsv(type) {
    // type: 'jobs' | 'applications' | 'support-cases' | 'companies' | 'job-seekers' | 'cognito-users'
    return apiClient.getText(`/admin/export/${type}/csv`);
  }

  // Send a push notification to all subscribers (admin only)
  async sendPush(payload) {
    return apiClient.post('/push/send', { payload });
  }

  // Get stored push subscriptions (admin only)
  async getSubscriptions() {
    return apiClient.get('/push/subscriptions');
  }

  // ── Invoice Usage Stats ────────────────────────────────────────────────────
  async getInvoiceUsage() {
    return apiClient.get('/admin/invoice-usage');
  }

  // ── Feedbacks (Admin) ─────────────────────────────────────────────────────
  async getAllFeedbacks() {
    return apiClient.get('/feedback/admin/all');
  }

  async updateFeedbackStatus(feedbackId, status) {
    return apiClient.patch(`/feedback/${feedbackId}/status`, { status });
  }

  // ── Secure Admin Navigation ────────────────────────────────────────────────
  async getAdminNavigationUrl(action, params = {}) {
    const qs = new URLSearchParams(params).toString()
    const query = qs ? `?${qs}` : ''
    return apiClient.get(`/admin/navigation/${action}${query}`)
  }
}

const adminService = new AdminService();
export default adminService;
